import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { useLanguage } from './LanguageContext';
import { getTranslation } from './translations';
import LanguageSelector from './LanguageSelector';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

// BackButton component
const BackButton = ({ language }) => {
  const navigate = useNavigate();
  
  return (
    <button
      onClick={() => navigate('/mecz/turniej-rankingowy')}
      style={{
        background: 'none',
        border: 'none',
        fontSize: 24,
        cursor: 'pointer',
        color: '#888',
        padding: 8,
        borderRadius: 4,
        transition: 'color 0.2s',
        textDecoration: 'none'
      }}
      onMouseOver={(e) => e.target.style.color = '#333'}
      onMouseOut={(e) => e.target.style.color = '#888'}
    >
      ←
    </button>
  );
};

// Breadcrumbs component
const Breadcrumbs = ({ language }) => {
  const location = window.location.pathname;
  const segments = location.split('/').filter(seg => seg);
  
  const crumbs = segments.map(seg => {
    const decodedSeg = decodeURIComponent(seg);
    return decodedSeg;
  });
  
  return (
    <div style={{ fontFamily: 'Nunito, Arial, sans-serif', fontWeight: 600, fontSize: window.innerWidth <= 600 ? 18 : 36, color: '#1e3a8a', textAlign: 'center', letterSpacing: 1 }}>
      {crumbs.map((seg, idx) => {
        let displayText;
        if (idx === 0 && seg === 'mecz') {
          displayText = getTranslation('menu', language);
        } else if (seg === 'turniej-rankingowy') {
          displayText = getTranslation('tournament', language);
        } else if (seg === 'statystyki-turniejow') {
          displayText = getTranslation('statystyki-turniejow', language);
        } else if (seg === 'mecz') {
          // Skip the intermediate 'mecz' segment
          return null;
        } else {
          displayText = seg;
        }
        
        return (
          <span key={idx}>
            {idx > 0 && <span style={{ margin: '0 8px' }}>/</span>}
            {displayText}
          </span>
        );
      }).filter(Boolean)}
    </div>
  );
};

const TournamentStats = () => {
  const { language } = useLanguage();
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    totalTournaments: 0,
    tournamentsThisYear: 0,
    totalGames: 0,
    gamesThisYear: 0,
    firstPlaces: 0,
    secondPlaces: 0,
    thirdPlaces: 0,
    topTenPlaces: 0,
    averagePercentile: 0,
    averagePercentileThisYear: 0
  });
  const [chartData, setChartData] = useState(null);

  useEffect(() => {
    const fetchTournamentStats = async () => {
      try {
        const token = sessionStorage.getItem('access_token');
        if (!token) {
          throw new Error(getTranslation('authError', language));
        }
        
        // Fetch both tournaments and games data
        const [openResponse, finishedResponse, gamesResponse] = await Promise.all([
          fetch('/api/tournaments/open', {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }),
          fetch('/api/tournaments/finished', {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }),
          fetch('/api/games', {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          })
        ]);
        
        if (!openResponse.ok) {
          throw new Error(`Błąd ${openResponse.status}: ${await openResponse.text()}`);
        }
        
        if (!finishedResponse.ok) {
          throw new Error(`Błąd ${finishedResponse.status}: ${await finishedResponse.text()}`);
        }
        
        if (!gamesResponse.ok) {
          throw new Error(`Błąd ${gamesResponse.status}: ${await gamesResponse.text()}`);
        }
        
        const openTournaments = await openResponse.json();
        const finishedTournaments = await finishedResponse.json();
        const tournamentsData = [...openTournaments, ...finishedTournaments];
        setTournaments(tournamentsData);
        
        const allGames = await gamesResponse.json();
        
        // Calculate statistics
        const currentYear = new Date().getFullYear();
        const finishedOnly = tournamentsData.filter(t => t.isfinished);
        const tournamentsThisYear = finishedOnly.filter(t => {
          const tournamentYear = new Date(t.tournamentdate).getFullYear();
          return tournamentYear === currentYear;
        });
        
        // Filter tournament games (those with tournament field) and specific roundtypes
        const tournamentGames = allGames.filter(game => 
          game.tournament !== null && 
          ['G', 'P', 'B'].includes(game.roundtype)
        );
        
        console.log('All games:', allGames.length);
        console.log('Tournament games (G,P,B):', tournamentGames.length);
        console.log('Sample tournament games:', tournamentGames.slice(0, 3));
        
        // Calculate total tournament games
        const totalGames = tournamentGames.length;
        
        // Calculate games this year
        const gamesThisYear = tournamentGames.filter(game => {
          const gameYear = new Date(game.gamedate).getFullYear();
          return gameYear === currentYear;
        }).length;
        
        // Calculate place statistics
        const places = finishedOnly.map(t => t.place).filter(p => p !== null);
        const firstPlaces = places.filter(p => p === 1).length;
        const secondPlaces = places.filter(p => p === 2).length;
        const thirdPlaces = places.filter(p => p === 3).length;
        const topTenPlaces = places.filter(p => p <= 10).length;
        
        // Calculate average percentile (how many players were worse than you)
        const averagePercentile = places.length > 0 ? 
          places.reduce((sum, place) => {
            // For each tournament, calculate what percentile this place represents
            const tournament = finishedOnly.find(t => t.place === place);
            if (tournament && tournament.totalplayers) {
              // If you're 2nd out of 20, then 18 players (90%) are worse than you
              const percentile = ((tournament.totalplayers - place) / tournament.totalplayers) * 100;
              return sum + percentile;
            }
            return sum;
          }, 0) / places.length : 0;
        
        // Calculate average percentile for this year only
        const finishedThisYear = finishedOnly.filter(t => {
          const tournamentYear = new Date(t.tournamentdate).getFullYear();
          return tournamentYear === currentYear;
        });
        
        const placesThisYear = finishedThisYear.map(t => t.place).filter(p => p !== null);
        const averagePercentileThisYear = placesThisYear.length > 0 ? 
          placesThisYear.reduce((sum, place) => {
            const tournament = finishedThisYear.find(t => t.place === place);
            if (tournament && tournament.totalplayers) {
              const percentile = ((tournament.totalplayers - place) / tournament.totalplayers) * 100;
              return sum + percentile;
            }
            return sum;
          }, 0) / placesThisYear.length : 0;
        
        setStats({
          totalTournaments: finishedOnly.length,
          tournamentsThisYear: tournamentsThisYear.length,
          totalGames: totalGames,
          gamesThisYear: gamesThisYear,
          firstPlaces: firstPlaces,
          secondPlaces: secondPlaces,
          thirdPlaces: thirdPlaces,
          topTenPlaces: topTenPlaces,
          averagePercentile: Math.round(averagePercentile),
          averagePercentileThisYear: Math.round(averagePercentileThisYear)
        });
        
        // Prepare chart data for last 50 tournaments from this year
        const last50Tournaments = finishedThisYear
          .sort((a, b) => new Date(b.tournamentdate) - new Date(a.tournamentdate))
          .slice(0, 50)
          .reverse(); // Reverse to show chronological order
        
        const chartLabels = last50Tournaments.map(t => 
          `${t.tournamentname?.substring(0, 15) || 'Turniej'} (${new Date(t.tournamentdate).getFullYear()})`
        );
        
        const chartPercentiles = last50Tournaments.map(t => {
          if (t.place && t.totalplayers) {
            // Don't round individual percentiles - only round the final average
            return ((t.totalplayers - t.place) / t.totalplayers) * 100;
          }
          return null;
        }).filter(p => p !== null);
        
        // Calculate running average - use the same method as averagePercentileThisYear
        const runningAverage = [];
        let sum = 0;
        chartPercentiles.forEach((percentile, index) => {
          sum += percentile;
          runningAverage.push(Math.round(sum / (index + 1)));
        });
        
        // Debug: Log the calculations to see the difference
        console.log('Chart percentiles:', chartPercentiles);
        console.log('Running average:', runningAverage);
        console.log('Last running average:', runningAverage[runningAverage.length - 1]);
        console.log('This year average:', averagePercentileThisYear);
        console.log('Rounded this year average:', Math.round(averagePercentileThisYear));
        
        const chartDataConfig = {
          labels: chartLabels,
          datasets: [
            {
              label: getTranslation('tournamentPercentile', language),
              data: chartPercentiles.map(p => Math.round(p)), // Round for display only
              borderColor: 'rgb(75, 192, 192)',
              backgroundColor: 'rgba(75, 192, 192, 0.2)',
              tension: 0.1,
              pointRadius: 3,
              pointHoverRadius: 5
            },
            {
              label: getTranslation('averagePercentile', language),
              data: runningAverage,
              borderColor: 'rgb(255, 99, 132)',
              backgroundColor: 'rgba(255, 99, 132, 0.2)',
              tension: 0.1,
              pointRadius: 2,
              pointHoverRadius: 4
            }
          ]
        };
        
        setChartData(chartDataConfig);
        
        setLoading(false);
        
      } catch (err) {
        console.error('Fetch error:', err);
        setError(`${getTranslation('connectionError', language)} ${err.message}`);
        setLoading(false);
      }
    };

    fetchTournamentStats();
  }, []);

  if (loading) {
    return (
      <div style={{ position: 'relative', minHeight: '100vh', padding: 20, background: '#f0f8ff' }}>
        <div style={{ position: 'absolute', top: '20px', right: '20px', zIndex: 1000 }}>
          <LanguageSelector />
        </div>
        <div style={{ textAlign: 'center', marginTop: '50px' }}>
          <div style={{ fontSize: '24px', color: '#666' }}>{getTranslation('loadingStats', language)}</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ position: 'relative', minHeight: '100vh', padding: 20, background: '#f0f8ff' }}>
        <div style={{ position: 'absolute', top: '20px', right: '20px', zIndex: 1000 }}>
          <LanguageSelector />
        </div>
        <div style={{ textAlign: 'center', marginTop: '50px' }}>
          <div style={{ fontSize: '24px', color: '#d32f2f' }}>{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ position: 'relative', minHeight: '100vh', padding: 20, background: '#f0f8ff' }}>
      {/* Language Selector */}
      <div style={{ position: 'absolute', top: '20px', right: '20px', zIndex: 1000 }}>
        <LanguageSelector />
      </div>
      
      {/* Main Title */}
      <h1 style={{
        fontSize: window.innerWidth <= 600 ? '2.5rem' : '3.5rem',
        fontWeight: 'bold',
        color: '#7c3aed',
        margin: '0 0 16px 0',
        textAlign: 'center',
        fontFamily: 'Nunito, Arial, sans-serif'
      }}>
        {getTranslation('shootMe', language)}
      </h1>
      
      {/* Back button and breadcrumbs in horizontal layout */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        gap: '20px',
        margin: '24px 0'
      }}>
        <BackButton language={language} />
        <Breadcrumbs language={language} />
      </div>

      {/* Statistics Cards */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: window.innerWidth <= 600 ? '1fr' : 'repeat(auto-fit, minmax(200px, 1fr))', 
        gap: '12px', 
        maxWidth: '1000px', 
        margin: '0 auto',
        padding: '0 20px'
      }}>
        {/* Tournament Statistics */}
        <div style={{
          background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
          padding: '16px',
          borderRadius: '12px',
          border: '1px solid #90caf9',
          textAlign: 'center',
          boxShadow: '0 2px 8px rgba(33, 150, 243, 0.2)'
        }}>
          <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#1565c0', marginBottom: '8px' }}>
            {stats.totalTournaments}
          </div>
          <div style={{ fontSize: '0.9rem', color: '#1976d2' }}>{getTranslation('allTournaments', language)}</div>
        </div>

        <div style={{
          background: 'linear-gradient(135deg, #e8f5e8 0%, #c8e6c9 100%)',
          padding: '16px',
          borderRadius: '12px',
          border: '1px solid #81c784',
          textAlign: 'center',
          boxShadow: '0 2px 8px rgba(76, 175, 80, 0.2)'
        }}>
          <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#2e7d32', marginBottom: '8px' }}>
            {stats.tournamentsThisYear}
          </div>
          <div style={{ fontSize: '0.9rem', color: '#388e3c' }}>{getTranslation('tournamentsThisYear', language)}</div>
        </div>

        {/* Game Statistics */}
        <div style={{
          background: 'linear-gradient(135deg, #fff3e0 0%, #ffcc80 100%)',
          padding: '16px',
          borderRadius: '12px',
          border: '1px solid #ffb74d',
          textAlign: 'center',
          boxShadow: '0 2px 8px rgba(255, 152, 0, 0.2)'
        }}>
          <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#ef6c00', marginBottom: '8px' }}>
            {stats.totalGames}
          </div>
          <div style={{ fontSize: '0.9rem', color: '#f57c00' }}>{getTranslation('allGames', language)}</div>
        </div>

        <div style={{
          background: 'linear-gradient(135deg, #fce4ec 0%, #f8bbd9 100%)',
          padding: '16px',
          borderRadius: '12px',
          border: '1px solid #f48fb1',
          textAlign: 'center',
          boxShadow: '0 2px 8px rgba(233, 30, 99, 0.2)'
        }}>
          <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#c2185b', marginBottom: '8px' }}>
            {stats.gamesThisYear}
          </div>
          <div style={{ fontSize: '0.9rem', color: '#d81b60' }}>{getTranslation('gamesThisYear', language)}</div>
        </div>

        {/* Place Statistics */}
        <div style={{
          background: 'linear-gradient(135deg, #fff8e1 0%, #ffecb3 100%)',
          padding: '16px',
          borderRadius: '12px',
          border: '1px solid #ffd54f',
          textAlign: 'center',
          boxShadow: '0 2px 8px rgba(255, 193, 7, 0.3)'
        }}>
          <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#f57f17', marginBottom: '8px' }}>
            {stats.firstPlaces}
          </div>
          <div style={{ fontSize: '0.9rem', color: '#ff8f00' }}>{getTranslation('firstPlaces', language)}</div>
        </div>

        <div style={{
          background: 'linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%)',
          padding: '16px',
          borderRadius: '12px',
          border: '1px solid #ce93d8',
          textAlign: 'center',
          boxShadow: '0 2px 8px rgba(156, 39, 176, 0.2)'
        }}>
          <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#7b1fa2', marginBottom: '8px' }}>
            {stats.secondPlaces}
          </div>
          <div style={{ fontSize: '0.9rem', color: '#8e24aa' }}>{getTranslation('secondPlaces', language)}</div>
        </div>

        <div style={{
          background: 'linear-gradient(135deg, #e0f2f1 0%, #b2dfdb 100%)',
          padding: '16px',
          borderRadius: '12px',
          border: '1px solid #80cbc4',
          textAlign: 'center',
          boxShadow: '0 2px 8px rgba(0, 150, 136, 0.2)'
        }}>
          <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#00695c', marginBottom: '8px' }}>
            {stats.thirdPlaces}
          </div>
          <div style={{ fontSize: '0.9rem', color: '#00796b' }}>{getTranslation('thirdPlaces', language)}</div>
        </div>

        <div style={{
          background: 'linear-gradient(135deg, #e8eaf6 0%, #c5cae9 100%)',
          padding: '16px',
          borderRadius: '12px',
          border: '1px solid #9fa8da',
          textAlign: 'center',
          boxShadow: '0 2px 8px rgba(63, 81, 181, 0.2)'
        }}>
          <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#3f51b5', marginBottom: '8px' }}>
            {stats.topTenPlaces}
          </div>
          <div style={{ fontSize: '0.9rem', color: '#5c6bc0' }}>{getTranslation('topTenPlaces', language)}</div>
        </div>

        {/* Percentile Statistics */}
        <div style={{
          background: 'linear-gradient(135deg, #f1f8e9 0%, #dcedc8 100%)',
          padding: '16px',
          borderRadius: '12px',
          border: '1px solid #aed581',
          textAlign: 'center',
          boxShadow: '0 2px 8px rgba(139, 195, 74, 0.2)',
          gridColumn: window.innerWidth <= 600 ? '1' : 'span 2'
        }}>
          <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#558b2f', marginBottom: '8px' }}>
            {language === 'pl' ? `Jesteś lepsza/y od ${stats.averagePercentile}%` : 
             language === 'en' ? `You are better than ${stats.averagePercentile}%` :
             `Vous êtes meilleur que ${stats.averagePercentile}%`}
          </div>
          <div style={{ fontSize: '0.9rem', color: '#689f38' }}>{getTranslation('percentileDescription', language)}</div>
        </div>

        {/* This Year Percentile Statistics */}
        <div style={{
          background: 'linear-gradient(135deg, #e8f5e8 0%, #a5d6a7 100%)',
          padding: '16px',
          borderRadius: '12px',
          border: '1px solid #81c784',
          textAlign: 'center',
          boxShadow: '0 2px 8px rgba(76, 175, 80, 0.2)',
          gridColumn: window.innerWidth <= 600 ? '1' : 'span 2'
        }}>
          <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#1b5e20', marginBottom: '8px' }}>
            {language === 'pl' ? `W tym roku jesteś lepsza/y od ${stats.averagePercentileThisYear}%` : 
             language === 'en' ? `This year you are better than ${stats.averagePercentileThisYear}%` :
             `Cette année vous êtes meilleur que ${stats.averagePercentileThisYear}%`}
          </div>
          <div style={{ fontSize: '0.9rem', color: '#2e7d32' }}>
            {getTranslation('thisYearPercentileDescription', language)} {new Date().getFullYear()})
          </div>
        </div>
      </div>

      {/* Percentile Chart */}
      {chartData && (
        <div style={{ 
          maxWidth: '1200px', 
          margin: '40px auto 0',
          padding: '0 20px'
        }}>
          <h2 style={{
            fontSize: '1.8rem',
            fontWeight: 'bold',
            color: '#333',
            marginBottom: '20px',
            textAlign: 'center',
            fontFamily: 'Nunito, Arial, sans-serif'
          }}>
            {getTranslation('percentileChart', language)}
          </h2>
          
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '20px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
            height: '400px'
          }}>
            <Line 
              data={chartData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  title: {
                    display: true,
                    text: getTranslation('tournamentComparison', language),
                    font: {
                      size: 16,
                      family: 'Nunito, Arial, sans-serif'
                    }
                  },
                  legend: {
                    position: 'top',
                    labels: {
                      font: {
                        family: 'Nunito, Arial, sans-serif'
                      }
                    }
                  },
                  tooltip: {
                    callbacks: {
                      label: function(context) {
                        return `${context.dataset.label}: ${context.parsed.y}%`;
                      }
                    }
                  }
                },
                scales: {
                  x: {
                    title: {
                      display: true,
                      text: getTranslation('tournamentsChronologically', language),
                      font: {
                        family: 'Nunito, Arial, sans-serif'
                      }
                    },
                    ticks: {
                      maxRotation: 45,
                      minRotation: 45,
                      font: {
                        family: 'Nunito, Arial, sans-serif',
                        size: 10
                      }
                    }
                  },
                  y: {
                    title: {
                      display: true,
                      text: getTranslation('percentile', language),
                      font: {
                        family: 'Nunito, Arial, sans-serif'
                      }
                    },
                    min: 0,
                    max: 100,
                    ticks: {
                      font: {
                        family: 'Nunito, Arial, sans-serif'
                      }
                    }
                  }
                }
              }}
            />
          </div>
        </div>
      )}

    </div>
  );
};

export default TournamentStats;
